import axios from 'axios';
import { getClerkToken } from 'helpers/requests';

/**
 * The admin console gets its OWN axios instance (D9).
 *
 * `helpers/requests.js` installs a global response interceptor on the DEFAULT
 * instance that redirects any 403 carrying `error: "AuthorizationError"` to
 * `/trial-expired`. On this origin that route does not exist, so a non-admin's 403
 * would bounce them into a dead redirect instead of rendering the access panel —
 * and the URL would change, which makes the failure look like a routing bug rather
 * than a permissions one. Interceptors registered on the default instance do not
 * apply to an `axios.create()` instance, so this sidesteps it by construction
 * rather than by remembering to special-case the status.
 *
 * `getClerkToken` is reused rather than reimplemented: its cache validation against
 * Clerk's active organisation is load-bearing and a second copy would rot.
 * Importing `helpers/requests` executes that module, registering the customer
 * interceptors on the default instance — harmless, since nothing here uses it.
 * That last clause was NOT true until the admin API grew its own `/country`
 * endpoint: `useAllCountries` goes through the default instance, so the console's
 * country lookup was subject to the customer 403 -> `/trial-expired` interceptor on
 * an origin with no such route. `src/admin-index.jsx` now seeds that cache through
 * THIS instance, which is what finally makes the sentence accurate. Do not
 * "simplify" the console back onto the customer country endpoint.
 *
 * ITS OWN ENVIRONMENT VARIABLE, and this is load-bearing (admin-api-split S2).
 * `REACT_APP_API_URL` is read by `helpers/requests.js` and `MagicLink.jsx` too, and
 * both builds resolve it from the SAME file — `Dockerfile` and `Dockerfile.admin`
 * each `COPY .env.${ENVIRONMENT} .env`, and locally both Vite configs call
 * `loadEnv(mode, process.cwd(), …)` with no mode override. So there is no value of
 * `REACT_APP_API_URL` that points this console at the admin API without also
 * pointing the CUSTOMER app at a backend that serves nothing but `/admin/*`. A
 * second variable is the only way to move one and not the other.
 *
 * The 8081 default matches the admin backend's own `.env.example`; the customer
 * backend owns 8080, and both run at once in development.
 */
const baseURL = process.env.REACT_APP_ADMIN_API_URL ?? 'http://localhost:8081';

// A base URL with no scheme is a config typo Chrome reports as something else entirely.
// `REACT_APP_ADMIN_API_URL=localhost:8081` makes axios build `localhost:8081/admin/...`, the
// browser reads `localhost:` as the PROTOCOL, and the failure arrives as:
//
//   blocked by CORS policy: Cross origin requests are only supported for protocol schemes:
//   chrome, chrome-extension, ..., http, https
//
// which sends whoever reads it into the server's CORS config — where there is nothing to find,
// because the request never left the browser. Say the real thing instead.
//
// Warn rather than repair: prepending a scheme means guessing http vs https, and guessing
// `http` for a deployed origin would downgrade the connection silently. This is a one-character
// fix in a .env file, so the right move is to name it and let it fail.
if (!/^https?:\/\//.test(baseURL)) {
  // eslint-disable-next-line no-console
  console.error(
    `[adminHttp] REACT_APP_ADMIN_API_URL is "${baseURL}", which has no http:// or https:// ` +
      'prefix. Every request will fail, and Chrome will report it as a CORS policy error ' +
      'against an unsupported protocol scheme — it is not CORS. Add the scheme, or remove the ' +
      'variable entirely to fall back to http://localhost:8081.',
  );
}

const adminHttp = axios.create({ baseURL });

adminHttp.interceptors.request.use(async (config) => {
  config.headers.Authorization = `Bearer ${await getClerkToken()}`;
  return config;
});

export default adminHttp;
